import React, { useEffect, useRef } from 'react';
import { EmptyState } from 'Atoms';
import {
    VStack,
    Box,
    Accordion,
    AccordionItem,
} from '@chakra-ui/react';
import { useTranslation } from 'utils/translation';
import { Article, } from './KnowledgeBase.hooks';
import { RefreshIcon } from 'Tokens/Icons/Function';
import { ArticleResultItem } from './ArticleResultItem';

const Loader = () => {
    return (
        <Accordion width="100%">
            {[0, 1, 2, 3].map((i) => (
                <AccordionItem
                    width="100%"
                    key={i}
                    marginBottom="md"
                    borderWidth="1px"
                    borderColor="secondary.main"
                    outlineColor="secondary.main"
                    borderRadius="4px"
                    flex="1"
                >
                    <Box bg="#E0E0E0" w="100%" p={4} color="white"></Box>
                </AccordionItem>
            ))}
        </Accordion>
    );
};

export const ArticleAccordion = ({ articlesToShow, isLoading, setSearchValue,
    expandedArticleIndexes, focusedIndex, handleAccordionChange
}: {
    articlesToShow: Article[],
    isLoading: boolean,
    setSearchValue: (s: string) => void,
    expandedArticleIndexes: number[],
    focusedIndex: number,
    handleAccordionChange: (index: number) => void
}) => {
    const { t } = useTranslation('common');

    // Create a ref array matching the rendered items
    const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
    // Ensure the array has a slot per visible article
    useEffect(() => {
        itemRefs.current = new Array(articlesToShow.length).fill(null);
    }, [articlesToShow.length]);


    useEffect(() => {
        // If no focused index, do nothing
        const el = itemRefs.current[focusedIndex];
        if (!el) return;

        // Scroll to the focused element
        const id = requestAnimationFrame(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        });
        return () => cancelAnimationFrame(id);
    }, [expandedArticleIndexes, focusedIndex]);

    if (!isLoading) {
        return (
            <Accordion
                as={VStack}
                allowToggle
                allowMultiple
                width="100%"
                defaultIndex={articlesToShow.length === 1 ? 0 : undefined}
                index={expandedArticleIndexes}
                onChange={handleAccordionChange}
                spacing="12px"
            >
                {articlesToShow.length > 0 &&
                    articlesToShow.map((article, idx) =>
                        <ArticleResultItem
                            key={article.id}
                            article={article}
                            ref={(node) => void (itemRefs.current[idx] = node)}
                        />
                    )}
                {!isLoading && articlesToShow.length === 0 && (
                    <Box w="100%" h="100%" flexGrow="1">
                        <EmptyState
                            title={t('common:search.filter.emptyTitle')}
                            description={t('common:search.filter.emptyDescription')}
                            callToAction={{
                                text: t('common:search.filter.emptyBtn'),
                                variant: 'secondary',
                                onClick: () => {
                                    setSearchValue('');
                                },
                                leftIcon: <RefreshIcon color="inherit" />,
                            }}
                            component={true}
                        />
                    </Box>
                )}
            </Accordion>
        );
    }
    return <Loader />;
};
